import { Application, FileContent, FileContentFactory } from "../"
import { ModifyAdultFamilyMember, ModifyAdultFamilyMemberFactory } from "./adultFamilyMember.model"

export interface ModifyApplication {
    masterId: string
    employeeId: string
    version: number
    name: string
    surname: string
    oib: string
    residenceAddress: string
    mobile: string
    email: string
    numberOfFamilyMembers: number
    employeeMonthlyIncome: number
    isSingleParent: boolean
    numberOfChildren: number
    currentAccomodation: string
    employeePropertyDocument: FileContent
    ownsPropertyCloserThan30Km: boolean
    ownsPropertyFurtherThan30Km: boolean
    ownsLeaseAgreement: boolean
    lookingForApartment: boolean
    consentsToPrivacyPolicy: boolean
    adultFamilyMembers: ModifyAdultFamilyMember[]
    minorFamilyMembersBirthCertificates: FileContent[]
    singleParentConfirmation: FileContent
    residenceCertificate: FileContent
}

export class ModifyApplicationFactory {
    static from(application: Application): ModifyApplication {
        const adultFamilyMembers =
            application.adultFamilyMembers && application.adultFamilyMembers.length > 0
                ? application.adultFamilyMembers.map((member) => ModifyAdultFamilyMemberFactory.from(member))
                : application.numberOfFamilyMembers > 0
                    ? new Array(application.numberOfFamilyMembers).fill(null).map(() => ModifyAdultFamilyMemberFactory.create())
                    : [];

        const modifyApplication: ModifyApplication = JSON.parse(
            JSON.stringify({
                masterId: application.masterId,
                employeeId: application.employeeId,
                version: application.version,
                name: application.name,
                surname: application.surname,
                oib: application.oib,
                residenceAddress: application.residenceAddress,
                mobile: application.mobile,
                email: application.email,
                numberOfFamilyMembers: application.numberOfFamilyMembers,
                employeeMonthlyIncome: application.employeeMonthlyIncome ?? 0,
                isSingleParent: application.isSingleParent,
                numberOfChildren: application.numberOfChildren,
                currentAccomodation: application.currentAccomodation,
                employeePropertyDocument: FileContentFactory.fromUrl(application.employeePropertyDocument),
                ownsPropertyCloserThan30Km: application.ownsPropertyCloserThan30Km,
                ownsPropertyFurtherThan30Km: application.ownsPropertyFurtherThan30Km,
                ownsLeaseAgreement: application.ownsLeaseAgreement,
                lookingForApartment: application.lookingForApartment,
                consentsToPrivacyPolicy: application.consentsToPrivacyPolicy,
                adultFamilyMembers,
                minorFamilyMembersBirthCertificates: resizeFileContents(
                    (application.minorFamilyMembersBirthCertificates && application.minorFamilyMembersBirthCertificates.length > 0)
                        ? application.minorFamilyMembersBirthCertificates.map(file => FileContentFactory.fromUrl(file))
                        : [],
                    application.numberOfChildren,
                ),
                singleParentConfirmation: FileContentFactory.fromUrl(application.singleParentConfirmation),
                residenceCertificate: FileContentFactory.fromUrl(application.residenceCertificate),
            })
        )
        return modifyApplication
    }
}

enum ModifyApplicationKeys {
    Name = "name",
    Surname = "surname",
    OIB = "oib",
    ResidenceAddress = "residenceAddress",
    Mobile = "mobile",
    Email = "email",
    NumberOfFamilyMembers = "numberOfFamilyMembers",
    EmployeeMonthlyIncome = "employeeMonthlyIncome",
    IsSingleParent = "isSingleParent",
    NumberOfChildren = "numberOfChildren",
    CurrentAccomodation = "currentAccomodation",
    EmployeePropertyDocument = "employeePropertyDocument",
    OwnsPropertyCloserThan30Km = "ownsPropertyCloserThan30Km",
    OwnsPropertyFurtherThan30Km = "ownsPropertyFurtherThan30Km",
    OwnsLeaseAgreement = "ownsLeaseAgreement",
    LookingForApartment = "lookingForApartment",
    ConsentsToPrivacyPolicy = "consentsToPrivacyPolicy",
    AdultFamilyMembers = "adultFamilyMembers",
    MinorFamilyMembersBirthCertificates = "minorFamilyMembersBirthCertificates",
    SingleParentConfirmation = "singleParentConfirmation",
    ResidenceCertificate = "residenceCertificate",
}

export function resizeFileContents(
    current: FileContent[],
    newCount: number,
): FileContent[] {
    const resized = [...current]
    while (resized.length < newCount) {
        resized.push(FileContentFactory.create())
    }
    if (resized.length > newCount) {
        resized.length = newCount
    }
    return resized
}

export function ModifyApplicationKeysObject() {
    return {
        Name: ModifyApplicationKeys.Name,
        Surname: ModifyApplicationKeys.Surname,
        OIB: ModifyApplicationKeys.OIB,
        ResidenceAddress: ModifyApplicationKeys.ResidenceAddress,
        PhoneNumber: ModifyApplicationKeys.Mobile,
        Email: ModifyApplicationKeys.Email,
        NumberOfFamilyMembers: ModifyApplicationKeys.NumberOfFamilyMembers,
        EmployeeMonthlyIncome: ModifyApplicationKeys.EmployeeMonthlyIncome,
        IsSingleParent: ModifyApplicationKeys.IsSingleParent,
        NumberOfChildren: ModifyApplicationKeys.NumberOfChildren,
        CurrentAccomodation: ModifyApplicationKeys.CurrentAccomodation,
        EmployeePropertyDocument: ModifyApplicationKeys.EmployeePropertyDocument,
        OwnsPropertyCloserThan30Km: ModifyApplicationKeys.OwnsPropertyCloserThan30Km,
        OwnsPropertyFurtherThan30Km: ModifyApplicationKeys.OwnsPropertyFurtherThan30Km,
        OwnsLeaseAgreement: ModifyApplicationKeys.OwnsLeaseAgreement,
        LookingForApartment: ModifyApplicationKeys.LookingForApartment,
        ConsentsToPrivacyPolicy: ModifyApplicationKeys.ConsentsToPrivacyPolicy,
        AdultFamilyMembers: ModifyApplicationKeys.AdultFamilyMembers,
        MinorFamilyMembersBirthCertificates: ModifyApplicationKeys.MinorFamilyMembersBirthCertificates,
        SingleParentConfirmation: ModifyApplicationKeys.SingleParentConfirmation,
        ResidenceCertificate: ModifyApplicationKeys.ResidenceCertificate,
    }
}

export function EmptyModifyApplicationErrorsObject(): { [key: string]: boolean } {
    return {
        [ModifyApplicationKeys.Name]: false,
        [ModifyApplicationKeys.Surname]: false,
        [ModifyApplicationKeys.OIB]: false,
        [ModifyApplicationKeys.ResidenceAddress]: false,
        [ModifyApplicationKeys.Mobile]: false,
        [ModifyApplicationKeys.Email]: false,
        [ModifyApplicationKeys.NumberOfFamilyMembers]: false,
        [ModifyApplicationKeys.EmployeeMonthlyIncome]: false,
        [ModifyApplicationKeys.IsSingleParent]: false,
        [ModifyApplicationKeys.NumberOfChildren]: false,
        [ModifyApplicationKeys.CurrentAccomodation]: false,
        [ModifyApplicationKeys.EmployeePropertyDocument]: false,
        [ModifyApplicationKeys.OwnsPropertyCloserThan30Km]: false,
        [ModifyApplicationKeys.OwnsPropertyFurtherThan30Km]: false,
        [ModifyApplicationKeys.OwnsLeaseAgreement]: false,
        [ModifyApplicationKeys.LookingForApartment]: false,
        [ModifyApplicationKeys.ConsentsToPrivacyPolicy]: false,
        [ModifyApplicationKeys.AdultFamilyMembers]: false,
        [ModifyApplicationKeys.MinorFamilyMembersBirthCertificates]: false,
        [ModifyApplicationKeys.SingleParentConfirmation]: false,
        [ModifyApplicationKeys.ResidenceCertificate]: false,
    }
}
